import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AssetPreviewDto } from '@terra/shared-contracts-assets';
import { toggleIdsInSet } from '@terra/ui-design-system';

export const pruneSelectionToLoadedAssets = ({
  selectedAssetIds,
  assets,
}: {
  selectedAssetIds: ReadonlySet<string>;
  assets: readonly AssetPreviewDto[];
}): ReadonlySet<string> => {
  const loadedIds = new Set(assets.map((asset) => asset.id));
  const prunedIds = [...selectedAssetIds].filter((id) => loadedIds.has(id));

  return prunedIds.length === selectedAssetIds.size
    ? selectedAssetIds
    : new Set(prunedIds);
};

export function useAssetsTableSelection({
  assets,
  selectionScopeKey,
}: {
  assets: readonly AssetPreviewDto[];
  selectionScopeKey: string;
}) {
  const [selectedAssetIds, setSelectedAssetIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  useEffect(() => {
    setSelectedAssetIds(new Set());
  }, [selectionScopeKey]);

  useEffect(() => {
    setSelectedAssetIds((previous) =>
      pruneSelectionToLoadedAssets({ selectedAssetIds: previous, assets }),
    );
  }, [assets]);

  const selectedAssets = useMemo(
    () => assets.filter((asset) => selectedAssetIds.has(asset.id)),
    [assets, selectedAssetIds],
  );

  const setAssetIdsSelected = useCallback(
    ({
      assetIds,
      selected,
    }: {
      assetIds: readonly string[];
      selected: boolean;
    }) =>
      setSelectedAssetIds((previous) =>
        toggleIdsInSet({
          ids: previous,
          itemIds: assetIds,
          shouldInclude: selected,
        }),
      ),
    [],
  );

  const selectAllLoaded = useCallback(
    (selected: boolean) =>
      setSelectedAssetIds(
        selected ? new Set(assets.map((asset) => asset.id)) : new Set(),
      ),
    [assets],
  );

  const clearSelection = useCallback(() => {
    setSelectedAssetIds(new Set());
  }, []);

  return {
    selectedAssetIds,
    selectedAssets,
    setAssetIdsSelected,
    selectAllLoaded,
    clearSelection,
  };
}
